import React from 'react';

import { Box, Chip, Divider, Link, Typography } from '@mui/material';

import { shape } from '@app/theme';

import type { ReportSidebarProperties } from './ReportSidebar.types';
import type { AnalysisSection } from '@api/aiReviews/api.aiReviews.reviewTypes';



type NavItem = {
  id: string;
  label: string;
  score?: { value: number; max: number };
};

const getScoreColor = (score: number, maxScore: number): 'success' | 'warning' | 'error' => {
  const normalized = maxScore > 0 ? (score / maxScore) * 10 : 0;
  if (normalized >= 7) return 'success';
  if (normalized >= 5) return 'warning';
  return 'error';
};

function buildNavItems(review: ReportSidebarProperties['review']): {
  summary: NavItem[];
  sections: NavItem[];
  details: NavItem[];
} {
  const sections = review.sections ?? [];
  const risks = review.risks ?? [];
  const deal_breakers = review.deal_breakers ?? [];
  const due_diligence = review.due_diligence;

  const summaryItems: NavItem[] = [
    { id: 'report-thesis', label: 'Инвестиционный тезис' },
  ];

  if ((review.summary.key_metrics ?? []).length > 0) {
    summaryItems.push({ id: 'report-metrics', label: 'Ключевые метрики' });
  }
  if ((review.summary.strengths ?? []).length > 0 || (review.summary.critical_risks ?? []).length > 0) {
    summaryItems.push({ id: 'report-sw', label: 'Сильные / слабые' });
  }

  const sorted = [...sections].toSorted((a: AnalysisSection, b: AnalysisSection) => a.order - b.order);
  const sectionItems: NavItem[] = sorted.map(s => ({
    id: `section-${s.id}`,
    label: s.title,
    score: { value: s.score, max: s.max_score },
  }));

  const detailItems: NavItem[] = [ {
    id: 'report-scores',
    label: 'Итоговая оценка',
    score: { value: review.scores.overall, max: review.scores.max_score },
  }];
  if (risks.length > 0) {
    detailItems.push({ id: 'report-risks', label: 'Риски' });
  }
  if (deal_breakers.length > 0) {
    detailItems.push({ id: 'report-deal-breakers', label: 'Go / No-Go' });
  }
  if (due_diligence &&
      Object.values(due_diligence).some(
        (q) => Array.isArray(q) && q.length > 0,
      )) {
    detailItems.push({ id: 'report-dd', label: 'Due Diligence' });
  }

  return { summary: summaryItems, sections: sectionItems, details: detailItems };
}

const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant='caption'
    sx={{
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'text.muted',
      display: 'block',
      px: 1.5,
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

export const ReportSidebar: React.FC<ReportSidebarProperties> = ({
  review,
  dataTest = 'report_sidebar',
}) => {
  const [activeId, setActiveId] = React.useState<string>('');
  const { summary, sections, details } = React.useMemo(() => buildNavItems(review), [review]);
  const allItems = React.useMemo(() => [...summary, ...sections, ...details], [summary, sections, details]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      {
        rootMargin: '-100px 0px -60% 0px',
        threshold: 0,
      },
    );

    for (const item of allItems) {
      const element = document.querySelector(`#${item.id}`);
      if (element) observer.observe(element);
    }

    return () => observer.disconnect();
  }, [allItems]);

  const handleClick = React.useCallback(
    (event: React.MouseEvent, id: string) => {
      event.preventDefault();
      const element = document.querySelector(`#${id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveId(id);
      }
    },
    [],
  );

  const renderItem = (item: NavItem) => {
    const isActive = activeId === item.id;
    return (
      <Link
        key={item.id}
        href={`#${item.id}`}
        underline='none'
        onClick={event => handleClick(event, item.id)}
        data-testid={`${dataTest}_item_${item.id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: `${shape.borderRadius}px`,
          color: isActive ? 'primary.main' : 'text.secondary',
          backgroundColor: isActive ? 'action.selected' : 'transparent',
          fontSize: '0.8125rem',
          fontWeight: isActive ? 600 : 500,
          transition: 'all 0.15s ease',
          textDecoration: 'none',
          '&:hover': {
            backgroundColor: 'action.hover',
            color: 'text.primary',
          },
        }}
      >
        <Typography
          component='span'
          sx={{
            flex: 1,
            fontSize: '0.8125rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.label}
        </Typography>

        {item.score && (
          <Chip
            label={`${item.score.value}/${item.score.max}`}
            color={getScoreColor(item.score.value, item.score.max)}
            size='small'
            variant='outlined'
            sx={{
              height: 22,
              fontSize: '0.6875rem',
              fontWeight: 600,
              borderWidth: 1.5,
              borderRadius: `${shape.borderRadius}px`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Link>
    );
  };

  return (
    <Box
      component='nav'
      data-testid={dataTest}
      sx={{
        position: 'sticky',
        top: 100,
        alignSelf: 'flex-start',
        width: 220,
        flexShrink: 0,
        display: { xs: 'none', lg: 'block' },
      }}
    >
      {/* Резюме */}
      <GroupLabel>Резюме</GroupLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5 }}>
        {summary.map((item) => renderItem(item))}
      </Box>

      {/* Разделы анализа */}
      {sections.length > 0 && (
        <>
          <Divider sx={{ mb: 1.5 }} />
          <GroupLabel>Детальный анализ</GroupLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5 }}>
            {sections.map((item) => renderItem(item))}
          </Box>
        </>
      )}

      {/* Итоги */}
      {details.length > 0 && (
        <>
          <Divider sx={{ mb: 1.5 }} />
          <GroupLabel>Итоги</GroupLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {details.map((item) => renderItem(item))}
          </Box>
        </>
      )}
    </Box>
  );
};
